import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { ROUTES } from '../../../constants/routes';
import { generateUrl } from '../../util';
import { RefreshType, NavCode, TemplateHistory } from '@features/survey-templates/types';
import { useToggle } from '@hooks/useToggle';
import { DeleteConfirmation } from '@g17eco/molecules/confirm-modal';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { useBulkSurveysDeleteMutation, useBulkSurveysRefreshStatusQuery } from '@api/survey-templates-history';
import { useSiteAlert } from '../../../hooks/useSiteAlert';
import { SURVEY } from '@constants/terminology';
import { SurveyType } from '@g17eco/types/surveyCommon';
import { JobStatus } from '@g17eco/types/background-jobs';

interface TemplateHistoryOptionsProps {
  templateHistory: TemplateHistory;
  isRefreshing: boolean;
  isCheckingDiff: boolean;
  initiateRefresh: (params: { historyId: string; refreshType: RefreshType }) => void;
  disabled?: boolean;
}

const RefreshJobAwareDropdownItem = ({
  isLoading,
  children,
  onClick,
  ...props
}: { isLoading: boolean; children: React.ReactNode; onClick: () => void } & React.ComponentProps<
  typeof DropdownItem
>) => {
  return (
    <SimpleTooltip text={isLoading ? 'A refresh process is currently running' : undefined}>
      <DropdownItem disabled={isLoading} onClick={onClick} {...props}>
        {children}
      </DropdownItem>
    </SimpleTooltip>
  );
};

export const TemplateHistoryOptions = (props: TemplateHistoryOptionsProps) => {
  const { templateHistory, isRefreshing, isCheckingDiff, initiateRefresh, disabled = false } = props;
  const [isOpen, toggleDropdown] = useToggle(false);
  const [pollingInterval, setPollingInterval] = useState(0);

  const {
    data: { isRunning: isRefreshJobRunning } = { isRunning: false },
    isFetching: isFetchingRefreshJobStatus,
    isError: isRefreshJobStatusError,
  } = useBulkSurveysRefreshStatusQuery(
    {
      templateId: templateHistory.templateId,
      historyId: templateHistory._id,
    },
    {
      pollingInterval,
      skip: !isOpen,
      refetchOnMountOrArgChange: true,
    },
  );

  useEffect(() => {
    setPollingInterval(isRefreshJobRunning ? 5000 : 0);
  }, [isRefreshJobRunning]);
  const [isConfirmingDelete, toggleDelete] = useToggle(false);
  const history = useHistory();
  const { initiativeId = '', templateId = '' } = useParams<{ initiativeId: string; templateId: string }>();
  const [bulkDeleteSurveys, { isLoading: isDeleting }] = useBulkSurveysDeleteMutation();
  const { addSiteAlert, addSiteError } = useSiteAlert();
  const disableRefresh =
    isDeleting ||
    isRefreshing ||
    isCheckingDiff ||
    isRefreshJobRunning ||
    isFetchingRefreshJobStatus ||
    isRefreshJobStatusError;

  const handleGoTo = (page: NavCode) => {
    return history.push(
      `${generateUrl(ROUTES.SURVEY_TEMPLATES_HISTORY, {
        initiativeId,
        templateId,
        historyId: templateHistory._id,
        page,
      })}`,
    );
  };

  const handleDelete = async () => {
    try {
      await bulkDeleteSurveys({ templateId, historyId: templateHistory._id });
      addSiteAlert({ content: `Successfully started a bulk ${SURVEY.PLURAL} deletion process` });
    } catch (e) {
      addSiteError(e);
    }
  };

  return (
    <Dropdown isOpen={isOpen} toggle={toggleDropdown}>
      <SimpleTooltip text="Access this template's configuration, units or scope">
        <DropdownToggle disabled={disabled || isDeleting} color='transparent' outline className='px-2'>
          <i className='fa-solid fa-bars' />
        </DropdownToggle>
      </SimpleTooltip>
      <DropdownMenu>
        <DropdownItem onClick={() => handleGoTo(NavCode.Configuration)}>
          <i className='fa-light fa-layer-group mr-2' />
          Template used
        </DropdownItem>
        <DropdownItem onClick={() => handleGoTo(NavCode.History)}>
          <i className='fa-solid fa-exclamation-triangle mr-2' />
          View failures
        </DropdownItem>
        <RefreshJobAwareDropdownItem
          isLoading={disableRefresh}
          onClick={() => initiateRefresh({ historyId: templateHistory._id, refreshType: RefreshType.Settings })}
        >
          <i className='fa-light fa-gear mr-2' />
          Refresh {SURVEY.SINGULAR} settings
        </RefreshJobAwareDropdownItem>
        {templateHistory.snapshot.type === SurveyType.Default ? (
          <RefreshJobAwareDropdownItem
            isLoading={disableRefresh}
            onClick={() => initiateRefresh({ historyId: templateHistory._id, refreshType: RefreshType.Scope })}
          >
            <i className='fa-light fa-pencil mr-2' />
            Refresh scope
          </RefreshJobAwareDropdownItem>
        ) : null}
        <RefreshJobAwareDropdownItem
          isLoading={disableRefresh}
          onClick={() => initiateRefresh({ historyId: templateHistory._id, refreshType: RefreshType.Workgroup })}
        >
          <i className='fa-light fa-users mr-2' />
          Refresh delegated workgroups
        </RefreshJobAwareDropdownItem>
        <RefreshJobAwareDropdownItem isLoading={disableRefresh} onClick={toggleDelete}>
          <i className='fa-solid fa-trash text-ThemeDangerMedium mr-2' />
          Delete {templateHistory.results.filter((result) => result.status === JobStatus.Completed).length}{' '}
          {SURVEY.SINGULAR}(s)
        </RefreshJobAwareDropdownItem>
      </DropdownMenu>
      {isConfirmingDelete ? (
        <DeleteConfirmation
          title={`Delete ${SURVEY.PLURAL}`}
          text={`Are you sure? Once these ${SURVEY.PLURAL} have been deleted they cannot be recovered.`}
          toggle={toggleDelete}
          handleConfirm={handleDelete}
        />
      ) : null}
    </Dropdown>
  );
};
